(() => {
  if (window.__lmpFeaturedProductsLoaded) return;
  window.__lmpFeaturedProductsLoaded = true;

  const rootSelector = '[data-e-featured-products]';

  const init = (root) => {
    if (!root || root.dataset.eProductsReady === 'true') return;

    const viewport = root.querySelector('[data-e-products-viewport]');
    const previousButton = root.querySelector('[data-e-products-prev]');
    const nextButton = root.querySelector('[data-e-products-next]');
    const hasNavigationButtons = Boolean(previousButton || nextButton);

    if (!viewport) return;

    let measureFrame = 0;

    const updateButtons = () => {
      const clientWidth = viewport.clientWidth;
      const scrollWidth = viewport.scrollWidth;
      const scrollLeft = hasNavigationButtons ? viewport.scrollLeft : 0;
      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      const hasOverflow = maxScroll > 2;

      root.dataset.eProductsOverflow = String(hasOverflow);

      if (previousButton) {
        const disabled = !hasOverflow || scrollLeft <= 2;
        previousButton.disabled = disabled;
        previousButton.setAttribute('aria-disabled', String(disabled));
      }

      if (nextButton) {
        const disabled = !hasOverflow || scrollLeft >= maxScroll - 2;
        nextButton.disabled = disabled;
        nextButton.setAttribute('aria-disabled', String(disabled));
      }
    };

    const scheduleUpdate = () => {
      if (measureFrame) return;
      measureFrame = window.requestAnimationFrame(() => {
        measureFrame = 0;
        updateButtons();
      });
    };

    const scrollPage = (direction) => {
      const distance = Math.max(180, viewport.clientWidth * 0.92);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      viewport.scrollBy({
        left: direction * distance,
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    };

    previousButton?.addEventListener('click', () => scrollPage(-1));
    nextButton?.addEventListener('click', () => scrollPage(1));

    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragging = false;
    let suppressClick = false;

    const finishDrag = (event) => {
      if (pointerId === null || (event?.pointerId != null && event.pointerId !== pointerId)) return;

      if (dragging) {
        suppressClick = true;
        window.setTimeout(() => {
          suppressClick = false;
        }, 180);
      }

      viewport.classList.remove('is-dragging');
      dragging = false;
      pointerId = null;
      if (hasNavigationButtons) scheduleUpdate();
    };

    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (root.dataset.eProductsOverflow !== 'true') return;
      if (event.target.closest('a, button, input, select, textarea, [role="button"]')) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = viewport.scrollLeft;
      dragging = false;
      viewport.setPointerCapture?.(pointerId);
    });

    viewport.addEventListener('pointermove', (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;

      const movement = event.clientX - startX;
      if (!dragging && Math.abs(movement) > 5) {
        dragging = true;
        viewport.classList.add('is-dragging');
      }

      if (!dragging) return;
      event.preventDefault();
      viewport.scrollLeft = startScrollLeft - movement;
    });

    viewport.addEventListener('pointerup', finishDrag);
    viewport.addEventListener('pointercancel', finishDrag);
    viewport.addEventListener('lostpointercapture', finishDrag);

    viewport.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    viewport.addEventListener('dragstart', (event) => {
      if (event.target.closest('a, img')) event.preventDefault();
    });

    /* Scroll position only affects arrow state. Without arrows there is no
       reason to perform layout reads on every horizontal scroll. */
    if (hasNavigationButtons) {
      viewport.addEventListener('scroll', scheduleUpdate, { passive: true });
    }

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(scheduleUpdate);
      observer.observe(viewport);
    } else {
      window.addEventListener('resize', scheduleUpdate, { passive: true });
    }

    root.dataset.eProductsReady = 'true';
    scheduleUpdate();
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(rootSelector)) init(scope);
    scope.querySelectorAll?.(rootSelector).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();